import { useMemo } from 'react';
import { LOG_ENTRY_TYPE_LABELS, LogEntryType } from '../../../../../types/SystemLogging.types';
import { FilterDropdown } from '../../../../ui/FilterDropdown/FilterDropdown';
import { FilterOption } from '../../../../ui/FilterDropdown/FilterDropdown.types';
import { AppliedFilters } from '../../../../ui/AppliedFilters/AppliedFilters';
import DatePicker from '../../../../ui/DatePicker/DatePicker';
import { MonitoringFiltersSectionProps } from './MonitoringFiltersSection.types';
import { toEndOfDayISOString, toStartOfDayISOString } from '../../../../../utils/Date.util';

// GET /events (this page's data source) only ever returns 'audit-ai-system'
// | 'repo-sync' - 'audit-organization'/'global' never apply to a single AI
// system, so they're left out here even though the org-wide Audit Log page
// offers them. Same labels as that page's "Type" filter (LOG_ENTRY_TYPE_LABELS),
// so the two stay worded identically.
const monitoringTypeOptions: FilterOption[] = [
  { value: 'audit-ai-system', label: LOG_ENTRY_TYPE_LABELS['audit-ai-system'] },
  { value: 'repo-sync', label: LOG_ENTRY_TYPE_LABELS['repo-sync'] },
];

export function MonitoringFiltersSection({
  selectedTypes,
  onTypesChange,
  selectedUser,
  onUserChange,
  selectedDateRange,
  onDateRangeChange,
  onResetFilters,
  userOptions = [],
  usersLoading = false,
}: MonitoringFiltersSectionProps) {
  const hasActiveFilters = useMemo(() => {
    return (
      selectedTypes.length > 0 ||
      selectedUser.length > 0 ||
      selectedDateRange !== null
    );
  }, [selectedTypes, selectedUser, selectedDateRange]);

  return (
    <div className="flex items-center gap-2 w-full">
      <AppliedFilters />

      <div className="flex-1 min-w-0">
        <FilterDropdown
          placeholder="Type"
          options={monitoringTypeOptions}
          selectedValues={selectedTypes}
          onSelectionChange={(values) => onTypesChange(values as LogEntryType[])}
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0">
        <FilterDropdown
          placeholder={usersLoading ? 'Loading...' : 'User'}
          options={userOptions}
          selectedValues={selectedUser}
          onSelectionChange={onUserChange}
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0">
        <DatePicker
          placeholder="Date"
          value={
            selectedDateRange
              ? {
                  startDate: new Date(selectedDateRange.startDate),
                  endDate: new Date(selectedDateRange.endDate),
                }
              : null
          }
          onDateRangeChange={(range) => {
            if (range.startDate && range.endDate) {
              onDateRangeChange({
                startDate: toStartOfDayISOString(range.startDate),
                endDate: toEndOfDayISOString(range.endDate),
              });
            } else {
              onDateRangeChange(null);
            }
          }}
          className="w-full"
        />
      </div>

      <button
        className={`flex items-center gap-2 px-3 py-2 flex-shrink-0 transition-colors ${
          hasActiveFilters ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
        }`}
        onClick={hasActiveFilters ? onResetFilters : undefined}
        disabled={!hasActiveFilters}
      >
        <span
          className={`text-xs leading-5 font-medium ${
            hasActiveFilters ? 'text-purple-800' : 'text-gray-200'
          }`}
        >
          Reset Filters
        </span>
      </button>
    </div>
  );
}
